import { useState } from 'react'
import { ExternalLink, Shield, Plus } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ErrorState } from '@/components/shared/ErrorState'
import {
  useAdminFrameworks,
  useCreateFramework,
  usePatchAdminFramework,
  useArchiveFramework,
  useUnarchiveFramework,
  useFrameworkReviewers,
  type AdminFrameworkRow,
  type CreateFrameworkInput,
} from '@/hooks/useAdminFrameworks'
import type { FrameworkCoverageStatus } from '@/hooks/useRegulatoryQueries'
import { formatDate } from '@/lib/utils'

/**
 * /admin/frameworks — framework definition management (KAN-271 s5).
 *
 * Lists every framework with provenance + detail/archive columns and lets
 * admins create, edit (definition incl. detail_enabled / reg_citation /
 * framework_type), archive, and un-archive framework definitions. Rows where
 * reviewed_at is null show an "Unreviewed" badge; archived rows show an
 * "Archived" badge. All mutations write structured audit rows server-side.
 */

const COVERAGE_OPTIONS: FrameworkCoverageStatus[] = [
  'full',
  'partial',
  'emerging',
  'pending-ingest',
]

const STATUS_CLASS: Record<string, string> = {
  full: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
  partial: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  emerging: 'bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300',
  'pending-ingest': 'bg-muted text-muted-foreground',
}

const STATUS_LABEL: Record<string, string> = {
  full: 'Full',
  partial: 'Partial',
  emerging: 'Emerging',
  'pending-ingest': 'Pending',
}

function UnreviewedBadge() {
  return (
    <span
      data-testid="unreviewed-badge"
      className="inline-flex items-center gap-1 rounded-full bg-orange-100 px-2 py-0.5 text-xs font-semibold text-orange-800 dark:bg-orange-900/30 dark:text-orange-300"
      title="This framework has not been reviewed — no provenance recorded"
    >
      ⚠ Unreviewed
    </span>
  )
}

function ArchivedBadge() {
  return (
    <span
      data-testid="archived-badge"
      className="inline-flex items-center gap-1 rounded-full bg-zinc-200 px-2 py-0.5 text-xs font-semibold text-zinc-700 dark:bg-zinc-700/40 dark:text-zinc-300"
      title="This framework is archived — hidden from the public landing list, KPI count and compare"
    >
      Archived
    </span>
  )
}

const COVERAGE_FIELD_CLASS =
  'flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring'

type CreateDialogProps = {
  open: boolean
  onClose: () => void
}

function CreateDialog({ open, onClose }: CreateDialogProps) {
  const create = useCreateFramework()
  const [form, setForm] = useState<CreateFrameworkInput>({
    slug: '',
    name: '',
    shortDescription: '',
    jurisdiction: '',
    referenceUrl: '',
    coverageStatus: 'emerging',
    regCitation: '',
    frameworkType: '',
    detailEnabled: true,
  })
  const [error, setError] = useState<string | null>(null)

  const set = <K extends keyof CreateFrameworkInput>(key: K, value: CreateFrameworkInput[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }))

  const handleSave = async () => {
    setError(null)
    try {
      await create.mutateAsync({
        slug: form.slug.trim(),
        name: form.name.trim(),
        shortDescription: form.shortDescription.trim(),
        jurisdiction: form.jurisdiction.trim(),
        referenceUrl: form.referenceUrl.trim(),
        coverageStatus: form.coverageStatus,
        regCitation: form.regCitation?.trim() ? form.regCitation.trim() : null,
        frameworkType: form.frameworkType?.trim() ? form.frameworkType.trim() : null,
        detailEnabled: form.detailEnabled,
      })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Create failed')
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose() }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>New framework</DialogTitle>
          <DialogDescription>
            Define a new regulatory framework. Slug is permanent — it keys the
            public detail route and onebox previews.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="create-slug">Slug</Label>
              <Input
                id="create-slug"
                placeholder="dora"
                value={form.slug}
                onChange={(e) => set('slug', e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="create-name">Name</Label>
              <Input
                id="create-name"
                placeholder="DORA"
                value={form.name}
                onChange={(e) => set('name', e.target.value)}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="create-description">Short description</Label>
            <Textarea
              id="create-description"
              rows={2}
              value={form.shortDescription}
              onChange={(e) => set('shortDescription', e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="create-jurisdiction">Jurisdiction</Label>
              <Input
                id="create-jurisdiction"
                placeholder="EU"
                value={form.jurisdiction}
                onChange={(e) => set('jurisdiction', e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="create-coverage-status">Coverage</Label>
              <select
                id="create-coverage-status"
                className={COVERAGE_FIELD_CLASS}
                value={form.coverageStatus}
                onChange={(e) => set('coverageStatus', e.target.value as FrameworkCoverageStatus)}
              >
                {COVERAGE_OPTIONS.map((c) => (
                  <option key={c} value={c}>
                    {STATUS_LABEL[c] ?? c}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="create-reference-url">Reference URL</Label>
            <Input
              id="create-reference-url"
              placeholder="https://…"
              value={form.referenceUrl}
              onChange={(e) => set('referenceUrl', e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="create-reg-citation">Reg citation</Label>
              <Input
                id="create-reg-citation"
                placeholder="Regulation (EU) 2022/2554"
                value={form.regCitation ?? ''}
                onChange={(e) => set('regCitation', e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="create-framework-type">Framework type</Label>
              <Input
                id="create-framework-type"
                placeholder="regulation"
                value={form.frameworkType ?? ''}
                onChange={(e) => set('frameworkType', e.target.value)}
              />
            </div>
          </div>
          <div className="flex items-center justify-between rounded-md border px-3 py-2">
            <div>
              <Label htmlFor="create-detail-enabled">Detail page enabled</Label>
              <p className="text-xs text-muted-foreground">
                Gates the framework detail surface.
              </p>
            </div>
            <Switch
              id="create-detail-enabled"
              checked={form.detailEnabled ?? true}
              onCheckedChange={(v) => set('detailEnabled', v)}
            />
          </div>
          {error && (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            data-testid="create-framework-submit"
            onClick={handleSave}
            disabled={create.isPending}
          >
            {create.isPending ? 'Creating…' : 'Create framework'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

type EditDialogProps = {
  framework: AdminFrameworkRow | null
  onClose: () => void
}

function EditDialog({ framework, onClose }: EditDialogProps) {
  const patch = usePatchAdminFramework()
  const [name, setName] = useState(framework?.name ?? '')
  const [shortDescription, setShortDescription] = useState(framework?.shortDescription ?? '')
  const [jurisdiction, setJurisdiction] = useState(framework?.jurisdiction ?? '')
  const [referenceUrl, setReferenceUrl] = useState(framework?.referenceUrl ?? '')
  const [coverageStatus, setCoverageStatus] = useState<FrameworkCoverageStatus>(
    framework?.coverageStatus ?? 'emerging',
  )
  const [regCitation, setRegCitation] = useState(framework?.regCitation ?? '')
  const [frameworkType, setFrameworkType] = useState(framework?.frameworkType ?? '')
  const [detailEnabled, setDetailEnabled] = useState(framework?.detailEnabled ?? true)
  const reviewers = useFrameworkReviewers(framework != null)
  // KAN-809/KAN-848: the reviewer is picked by account id only; '' = not
  // reviewed. The typed addresses recorded before KAN-809 are retired and no
  // longer shown (KAN-848 step 4 drops their column).
  const initialReviewer = framework?.reviewedByUserId ?? ''
  const [reviewerChoice, setReviewerChoice] = useState(initialReviewer)
  const initialReviewedAtDate = framework?.reviewedAt
    ? new Date(framework.reviewedAt).toISOString().substring(0, 10)
    : ''
  const [reviewedAt, setReviewedAt] = useState(initialReviewedAtDate)
  const [sourceCitation, setSourceCitation] = useState(framework?.sourceCitation ?? '')
  const [error, setError] = useState<string | null>(null)

  if (!framework) return null

  const reviewerChanged = reviewerChoice !== initialReviewer

  /**
   * The review date to send (KAN-848, codex round 1):
   *  - a CLEARED reviewer clears the date ("Not reviewed" has no date);
   *  - an UNCHANGED date input is never re-sent, so an unrelated save keeps
   *    the stored full timestamp instead of truncating it to midnight; when a
   *    new reviewer is chosen it is re-sent as the original full timestamp;
   *  - an empty date with a reviewer chosen is omitted, so the API stamps now:
   *    the API defaults reviewed_at to now() only when the body carries
   *    reviewedByUserId and no reviewedAt, so handleSave re-sends the
   *    (possibly unchanged) reviewer whenever the date was cleared
   *    (KAN-876, Captain E21: clearing the date re-stamps it to now);
   *  - an empty date with no reviewer clears the date ("Not reviewed").
   */
  const reviewedAtPatch = (): { reviewedAt?: string | null } => {
    if (reviewerChanged && !reviewerChoice) return { reviewedAt: null }
    const dateChanged = reviewedAt !== initialReviewedAtDate
    if (!dateChanged) {
      return reviewerChanged && framework?.reviewedAt ? { reviewedAt: framework.reviewedAt } : {}
    }
    if (reviewedAt) return { reviewedAt: new Date(reviewedAt).toISOString() }
    return reviewerChoice ? {} : { reviewedAt: null }
  }

  // KAN-876: a cleared date with a reviewer chosen relies on the API's
  // reviewer-present stamp, so the reviewer must be in the body even when it
  // did not change; without it the PATCH carries no provenance field and the
  // old date silently stays.
  const dateClearedWithReviewer =
    reviewedAt === '' && reviewedAt !== initialReviewedAtDate && reviewerChoice !== ''
  const sendReviewer = reviewerChanged || dateClearedWithReviewer

  const handleSave = async () => {
    setError(null)
    try {
      await patch.mutateAsync({
        id: framework.id,
        data: {
          name: name.trim(),
          shortDescription: shortDescription.trim(),
          jurisdiction: jurisdiction.trim(),
          referenceUrl: referenceUrl.trim(),
          coverageStatus,
          regCitation: regCitation.trim() ? regCitation.trim() : null,
          frameworkType: frameworkType.trim() ? frameworkType.trim() : null,
          detailEnabled,
          // Sent only when the reviewer changes or the date is cleared
          // (KAN-876: re-stamp now), so saving an unrelated edit never wipes
          // or re-stamps provenance.
          ...(sendReviewer ? { reviewedByUserId: reviewerChoice || null } : {}),
          ...reviewedAtPatch(),
          sourceCitation: sourceCitation || null,
        },
      })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed')
    }
  }

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose() }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit framework</DialogTitle>
          <DialogDescription>
            <strong>{framework.name}</strong> ({framework.slug}) — definition,
            detail gate, and provenance.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="edit-name">Name</Label>
              <Input id="edit-name" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-jurisdiction">Jurisdiction</Label>
              <Input
                id="edit-jurisdiction"
                value={jurisdiction}
                onChange={(e) => setJurisdiction(e.target.value)}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="edit-description">Short description</Label>
            <Textarea
              id="edit-description"
              rows={2}
              value={shortDescription}
              onChange={(e) => setShortDescription(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="edit-coverage-status">Coverage</Label>
              <select
                id="edit-coverage-status"
                className={COVERAGE_FIELD_CLASS}
                value={coverageStatus}
                onChange={(e) => setCoverageStatus(e.target.value as FrameworkCoverageStatus)}
              >
                {COVERAGE_OPTIONS.map((c) => (
                  <option key={c} value={c}>
                    {STATUS_LABEL[c] ?? c}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-framework-type">Framework type</Label>
              <Input
                id="edit-framework-type"
                value={frameworkType}
                onChange={(e) => setFrameworkType(e.target.value)}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="edit-reference-url">Reference URL</Label>
            <Input
              id="edit-reference-url"
              value={referenceUrl}
              onChange={(e) => setReferenceUrl(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="edit-reg-citation">Reg citation</Label>
            <Input
              id="edit-reg-citation"
              value={regCitation}
              onChange={(e) => setRegCitation(e.target.value)}
            />
          </div>
          <div className="flex items-center justify-between rounded-md border px-3 py-2">
            <div>
              <Label htmlFor="edit-detail-enabled">Detail page enabled</Label>
              <p className="text-xs text-muted-foreground">
                Gates the framework detail surface.
              </p>
            </div>
            <Switch
              id="edit-detail-enabled"
              checked={detailEnabled}
              onCheckedChange={setDetailEnabled}
            />
          </div>

          <div className="space-y-4 border-t pt-3">
            <p className="text-xs font-semibold uppercase text-muted-foreground">Provenance</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="edit-reviewed-by">Reviewed by</Label>
                <select
                  id="edit-reviewed-by"
                  className={COVERAGE_FIELD_CLASS}
                  value={reviewerChoice}
                  disabled={reviewers.isLoading}
                  onChange={(e) => setReviewerChoice(e.target.value)}
                >
                  <option value="">Not reviewed</option>
                  {(reviewers.data?.reviewers ?? []).map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.displayName && r.displayName !== r.email
                        ? `${r.displayName} (${r.email})`
                        : r.email}
                    </option>
                  ))}
                </select>
                {reviewers.isError ? (
                  <p className="text-xs text-destructive">Couldn't load the account list.</p>
                ) : null}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="edit-reviewed-at">Reviewed at</Label>
                <Input
                  id="edit-reviewed-at"
                  type="date"
                  value={reviewedAt}
                  onChange={(e) => setReviewedAt(e.target.value)}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-source-citation">Source citation</Label>
              <Input
                id="edit-source-citation"
                placeholder="e.g. NIST SP 800-53 Rev 5, https://csrc.nist.gov/…"
                value={sourceCitation}
                onChange={(e) => setSourceCitation(e.target.value)}
              />
            </div>
          </div>

          {error && (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button data-testid="edit-framework-submit" onClick={handleSave} disabled={patch.isPending}>
            {patch.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function ArchiveAction({ framework }: { framework: AdminFrameworkRow }) {
  const archive = useArchiveFramework()
  const unarchive = useUnarchiveFramework()
  const isArchived = framework.archivedAt !== null
  const pending = archive.isPending || unarchive.isPending

  const handleClick = () => {
    if (isArchived) {
      unarchive.mutate(framework.id)
    } else {
      archive.mutate(framework.id)
    }
  }

  return (
    <Button
      variant="ghost"
      size="sm"
      disabled={pending}
      data-testid={`${isArchived ? 'unarchive' : 'archive'}-button-${framework.slug}`}
      onClick={handleClick}
    >
      {isArchived ? 'Un-archive' : 'Archive'}
    </Button>
  )
}

export function FrameworksAdminPage() {
  const { data, isLoading, isError } = useAdminFrameworks()
  const [editing, setEditing] = useState<AdminFrameworkRow | null>(null)
  const [creating, setCreating] = useState(false)

  const rows = data?.frameworks ?? []
  const reviewedCount = rows.filter((r) => r.reviewedAt !== null).length
  const unreviewedCount = rows.length - reviewedCount
  const archivedCount = rows.filter((r) => r.archivedAt !== null).length

  if (isLoading) {
    return (
      <div className="space-y-3 p-6">
        {Array.from({ length: 8 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    )
  }

  if (isError) {
    return <ErrorState title="Unable to load frameworks" message="Check the API and try again." />
  }

  return (
    <div className="space-y-6 p-6">
      <header className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Shield className="h-5 w-5 text-muted-foreground" aria-hidden />
          <div>
            <h1 className="text-xl font-semibold">Regulatory frameworks</h1>
            <p className="text-sm text-muted-foreground">
              {rows.length} frameworks — {reviewedCount} reviewed,{' '}
              {unreviewedCount > 0 ? (
                <span className="font-semibold text-orange-600 dark:text-orange-400">
                  {unreviewedCount} unreviewed
                </span>
              ) : (
                <span className="text-emerald-600 dark:text-emerald-400">all reviewed</span>
              )}
              {archivedCount > 0 && (
                <>
                  {' · '}
                  <span className="text-muted-foreground">{archivedCount} archived</span>
                </>
              )}
            </p>
          </div>
        </div>
        <Button data-testid="create-framework-button" onClick={() => setCreating(true)}>
          <Plus className="mr-1.5 h-4 w-4" aria-hidden />
          New framework
        </Button>
      </header>

      <div className="overflow-x-auto rounded-md border">
        <table className="w-full text-sm">
          <thead className="border-b bg-muted/50">
            <tr>
              <th className="px-4 py-3 text-left font-medium">Framework</th>
              <th className="px-4 py-3 text-left font-medium">Jurisdiction</th>
              <th className="px-4 py-3 text-left font-medium">Coverage</th>
              <th className="px-4 py-3 text-left font-medium">Provenance</th>
              <th className="px-4 py-3 text-left font-medium">Source citation</th>
              <th className="px-4 py-3 text-right font-medium">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {rows.map((fw) => (
              <tr
                key={fw.id}
                data-testid={`framework-row-${fw.slug}`}
                className={`hover:bg-muted/30 ${fw.archivedAt ? 'opacity-60' : ''}`}
              >
                {/* Framework name + reference link */}
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{fw.name}</span>
                    {fw.archivedAt && <ArchivedBadge />}
                    <a
                      href={fw.referenceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-muted-foreground hover:text-primary"
                      title="Open reference"
                    >
                      <ExternalLink className="h-3 w-3" aria-hidden />
                    </a>
                  </div>
                  <p className="text-xs text-muted-foreground">{fw.slug}</p>
                </td>

                {/* Jurisdiction */}
                <td className="px-4 py-3">
                  <Badge variant="outline">{fw.jurisdiction}</Badge>
                </td>

                {/* Coverage status */}
                <td className="px-4 py-3">
                  <span
                    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATUS_CLASS[fw.coverageStatus] ?? 'bg-muted text-muted-foreground'}`}
                  >
                    {STATUS_LABEL[fw.coverageStatus] ?? fw.coverageStatus}
                  </span>
                </td>

                {/* Provenance — linked reviewer account + reviewed_at */}
                <td className="px-4 py-3">
                  {fw.reviewedAt ? (
                    <div>
                      <p className="text-xs font-medium">
                        {fw.reviewerDisplayName && fw.reviewerDisplayName !== fw.reviewerEmail
                          ? fw.reviewerDisplayName
                          : (fw.reviewerEmail ?? '—')}
                      </p>
                      <p className="text-xs text-muted-foreground">{formatDate(fw.reviewedAt)}</p>
                    </div>
                  ) : (
                    <UnreviewedBadge />
                  )}
                </td>

                {/* Source citation */}
                <td className="max-w-xs px-4 py-3">
                  {fw.sourceCitation ? (
                    <p
                      className="truncate text-xs text-muted-foreground"
                      title={fw.sourceCitation}
                    >
                      {fw.sourceCitation}
                    </p>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </td>

                {/* Actions */}
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button variant="ghost" size="sm" onClick={() => setEditing(fw)}>
                      Edit
                    </Button>
                    <ArchiveAction framework={fw} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <CreateDialog open={creating} onClose={() => setCreating(false)} />
      {/* Keyed so the form state is seeded from the framework being edited,
          not from the null it was first mounted with. */}
      <EditDialog key={editing?.id ?? 'none'} framework={editing} onClose={() => setEditing(null)} />
    </div>
  )
}
